import { useEffect, useRef } from 'react';

import { BodySmall } from '#/components/Text/BodySmall';
import { useAppConfig } from '#/config/AppConfigContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiSupportContactFormBlockData } from '#/modules/cms/api/types/blocks/CmsApiSupportContactFormBlock';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import {
  Container,
  Content,
  Panel,
  Summary,
  SummaryHeadline,
} from './CmsSupportContactFormBlock.styled';

type Props = {
  data: CmsApiSupportContactFormBlockData;
  hasSubmittedWithFeedback: boolean;
};

export function CmsSupportContactFormBlockSummary({
  data,
  hasSubmittedWithFeedback,
}: Props) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();

  const isFirstRun = useRef(true);
  const contentEl = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isFirstRun.current) return;
    if (!contentEl.current) return;

    isFirstRun.current = false;
    contentEl.current.scrollIntoView();
  }, []);

  const thankYouHeadlineText =
    data.thankYouHeadline ?? i18n('Thanks for submitting');
  let thankYouBodyText = data.thankYouBody ?? ''; // '' to please the type checker
  if (!thankYouBodyText && hasSubmittedWithFeedback) {
    thankYouBodyText = i18n(
      'We’ll make sure to consider your feedback and will get back to you when we can!',
    );
  }
  if (!thankYouBodyText && !hasSubmittedWithFeedback) {
    thankYouBodyText = i18n('Someone will respond to you shortly.');
  }

  return (
    <Container
      $imageHandle={data.bgImage?.handle}
      $mimetype={data.bgImage?.mimetype}
      $appConfig={appConfig}
    >
      <Content>
        <Panel>
          <div ref={contentEl} />
          <Summary>
            <img
              src={getStaticImageSrc(
                'illustrations/support-contact-form-summary.png',
                appConfig.idealist.staticImgPath,
              )}
              alt="An illustration of a paper plane, a checkmark in a chat bubble, and a green star sticker, suggesting communication, messaging, and achievement."
            />
            <SummaryHeadline text={thankYouHeadlineText} />
            <BodySmall dangerouslySetInnerHTML={{ __html: thankYouBodyText }} />
          </Summary>
        </Panel>
      </Content>
    </Container>
  );
}
